/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { Suspense, lazy } from 'preact/compat';
import { Container } from '@adobe-commerce/elsie/lib';
import { useMemo, useState } from 'preact/hooks';
import { AuthCombineProps, activeComponentType } from '@/auth/types';
import SkeletonLoader from '@/auth/components/SkeletonLoader';

const ResetPasswordForm = lazy(
  () => import('@/auth/components/ResetPasswordForm')
);
const SignInForm = lazy(() => import('@/auth/components/SignInForm'));
const SignUpForm = lazy(() => import('@/auth/components/SignUpForm'));

export const AuthCombine: Container<AuthCombineProps> = ({
  defaultView = 'signInForm',
  signInFormConfig,
  signUpFormConfig,
  resetPasswordFormConfig,
}) => {
  const [activeComponent, setActiveComponent] =
    useState<activeComponentType>(defaultView);

  const renderComponent = useMemo(
    () => ({
      signInForm: (
        <SignInForm
          setActiveComponent={setActiveComponent}
          {...signInFormConfig}
        />
      ),
      signUpForm: (
        <SignUpForm
          setActiveComponent={setActiveComponent}
          {...signUpFormConfig}
        />
      ),
      resetPasswordForm: (
        <ResetPasswordForm
          setActiveComponent={setActiveComponent}
          {...resetPasswordFormConfig}
        />
      ),
    }),
    [
      resetPasswordFormConfig,
      signInFormConfig,
      signUpFormConfig,
      setActiveComponent,
    ]
  );

  return (
    <div>
      <Suspense fallback={<SkeletonLoader activeSkeleton={activeComponent} />}>
        <div className="auth-combine">{renderComponent[activeComponent]}</div>
      </Suspense>
    </div>
  );
};
